import { media, typography } from "../../../../styles";
import React, { useEffect, useRef } from "react";
import SelectableButton, { SelectableButtonProps } from "../../../SelectableButton/SelectableButton";
import styled, { css } from "styled-components";
import { applyOpacity } from "../../../../styles/utils";
export type DateButtonProps = SelectableButtonProps & {
  column?: number;
  focused?: boolean;
  today?: boolean;
  unavailable?: boolean;
};
const unavailableStyles = css`
  color: ${({
  theme
}) => applyOpacity(theme.brand.cssVars["--d-text"], 0.28, false)} !important;
  /*
   * Was "background: theme.brand.cssVars['--d-surface'] !important" —
   * redundant with SelectableButton's own unselected-state background
   * (also --d-surface) in the common case, but it pinned a FLAT fill that
   * could visibly mismatch the ambient background behind the calendar
   * whenever that ambient isn't a flat --d-surface (e.g. a brand
   * pageBackground gradient), painting a stray rectangle across whichever
   * columns/rows contain unavailable dates. "transparent" lets unavailable
   * cells always match whatever they're sitting on, exactly like the
   * reference design (unavailable dates are just faded text, no fill).
   */
  background: transparent !important;
  border: none !important;
  cursor: default !important;
`;
export const StyledSelectableButton = styled(SelectableButton)<DateButtonProps>`
  ${({
  column
}) => column !== undefined ? css`
          grid-column-start: ${column};
        ` : null};

  width: 100%;
  height: 2.875rem;
  border: none;
  ${typography.body}

  @media ${media.hoverable} {
    &:hover {
      /*
       * Was --d-text. SelectableButton's own unselectedStyles hover pairs
       * "background: --d-tint" with "color: --d-on-tint" (a colour
       * computed to contrast with --d-tint specifically, since --d-tint is
       * always a light colour even on dark-theme brands). This override
       * left the tint background from SelectableButton but repainted the
       * text with --d-text, which is light/cream on the dark bucket —
       * light text on a light tint background, invisible. --d-on-tint
       * matches the background this rule is actually hovering on top of.
       */
      color: ${({
  theme
}) => theme.brand.cssVars["--d-on-tint"]};
      box-shadow: none;
      filter: brightness(0.97);
      border: 2px solid ${({
  theme
}) => theme.brand.cssVars["--d-primary"]};
    }
  }
  &:active {
    box-shadow: none;
    filter: brightness(0.97);
  }
  &:focus-visible {
    /*
     * Was &:focus — see SelectableButton's own &:focus-visible for why
     * plain :focus (which also matches after a mouse click) was replaced.
     * Kept in sync here so a mouse-clicked date doesn't get this coloured
     * border either, matching the ring no longer showing for that case.
     */
    border: 2px solid ${({
  theme
}) => theme.brand.cssVars["--d-primary"]};
  }

  ${({
  unavailable
}) => unavailable ? unavailableStyles : null}
  ${({
  selected,
  today
}) => today && !selected ? css`
          ${typography.bodyBold};
        ` : null}
`;
export function DateButton({
  focused,
  ...props
}: DateButtonProps) {
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (ref.current && focused) {
      ref.current.focus({
        preventScroll: true
      });
    }
  }, [focused]);
  return <StyledSelectableButton {...props} ref={ref} data-sentry-element="StyledSelectableButton" data-sentry-component="DateButton" data-sentry-source-file="DateButton.tsx" />;
}
export default DateButton;